import { useState } from 'react';
import { usePendingVendors, useApproveVendor, useRejectVendor } from '@/hooks/useAdmin';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { Check, X, Mail, MapPin, Calendar, Building2, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { VENDOR_CATEGORIES } from '@/types/vendor';

const PendingVendorsList = () => {
  const { data: vendors, isLoading, error } = usePendingVendors();
  const approveVendor = useApproveVendor();
  const rejectVendor = useRejectVendor();
  const { toast } = useToast();
  
  const [vendorToReject, setVendorToReject] = useState<string | null>(null);

  const handleApprove = async (vendorId: string, vendorName: string) => {
    try {
      await approveVendor.mutateAsync(vendorId);
      toast({
        title: "Proveedor aprobado",
        description: `${vendorName} ahora aparece en el directorio público.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo aprobar el proveedor.",
        variant: "destructive",
      });
    }
  };

  const handleReject = async () => {
    if (!vendorToReject) return;
    
    const vendor = vendors?.find(v => v.id === vendorToReject);
    
    try {
      await rejectVendor.mutateAsync(vendorToReject);
      toast({
        title: "Proveedor rechazado",
        description: `${vendor?.name} ha sido eliminado del sistema.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo rechazar el proveedor.",
        variant: "destructive",
      });
    } finally {
      setVendorToReject(null);
    }
  };

  const getCategoryName = (categoryId: string) => {
    const category = VENDOR_CATEGORIES.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  const getCategoryIcon = (categoryId: string) => {
    const category = VENDOR_CATEGORIES.find(c => c.id === categoryId);
    return category?.icon || '📦';
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar proveedores pendientes.</p>
        </CardContent>
      </Card>
    );
  }

  if (!vendors || vendors.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6 text-center">
          <Building2 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No hay proveedores pendientes de aprobación.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {vendors.map((vendor) => (
          <Card key={vendor.id} className="overflow-hidden">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {vendor.image && (
                    <img 
                      src={vendor.image} 
                      alt={vendor.name}
                      className="h-12 w-12 rounded-lg object-cover"
                    />
                  )}
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {vendor.name}
                      <a 
                        href={`/vendor/${vendor.slug}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1">
                      <span>{getCategoryIcon(vendor.category)}</span>
                      {getCategoryName(vendor.category)}
                    </CardDescription>
                  </div>
                </div>
                <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                  Pendiente
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {vendor.short_description && (
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {vendor.short_description}
                </p>
              )}
              
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                {vendor.contact_email && (
                  <div className="flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${vendor.contact_email}`} className="hover:text-primary">
                      {vendor.contact_email}
                    </a>
                  </div>
                )}
                {vendor.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {vendor.location}
                  </div>
                )}
                {vendor.created_at && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {format(new Date(vendor.created_at), "d 'de' MMMM, yyyy", { locale: es })}
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => handleApprove(vendor.id, vendor.name)}
                  disabled={approveVendor.isPending}
                  className="flex-1"
                  variant="default"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Aprobar
                </Button>
                <Button
                  onClick={() => setVendorToReject(vendor.id)}
                  disabled={rejectVendor.isPending}
                  variant="destructive"
                  className="flex-1"
                >
                  <X className="h-4 w-4 mr-2" />
                  Rechazar
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={!!vendorToReject} onOpenChange={() => setVendorToReject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Rechazar este proveedor?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará permanentemente al proveedor del sistema. 
              No podrá recuperarse después.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sí, rechazar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PendingVendorsList;
